/**
 * ChartPresentationToggle (#2546) — the compact, icon-only toggle for a chart's
 * PRESENTATION axis: how the same numbers are drawn. Ernteprognose uses it for
 * ``Linie | Säulen`` (#2546-B), Liquiditätsplanung for ``Kurve | Säulen``
 * (#2684-B).
 *
 * **The Preisspiegel ``Kerzen | Linie`` arm (#2546-A) is GONE** — HOF-6 §4
 * deleted candle mode outright ("candles didn't work for the users"), so every
 * mention of it below is a historical example, kept because the contract points
 * it motivated are still the contract. Do not go looking for the code.
 *
 * Deliberately chart-agnostic and generic over its value type: it knows nothing
 * about recharts, row models, series or URL params. Callers own the vocabulary,
 * the icons, the German copy and the persistence; this renders flags and fires a
 * callback, exactly like its ``ChartZoomControls`` sibling in this directory
 * (stateless, ``role="group"`` + German ``aria-label``, icon-only buttons with an
 * ``aria-label`` + ``title``, disabled when unavailable, lucide icons at 16px).
 * The two therefore read as one toolbar cluster wherever they sit together.
 *
 * Two design points that are load-bearing rather than incidental:
 *
 *  1. **Re-clicking the pressed option still fires ``onChange``.** Ernteprognose's
 *     default presentation depends on the active data mode, so "Linie because I
 *     chose it" and "Linie because Kumuliert implies it" are different states.
 *     Clicking the already-active option is precisely how a user promotes the
 *     derived default into an explicit choice, so this component must not
 *     short-circuit on ``value === option.value`` — that would make the explicit
 *     state unreachable from the UI. Callers that genuinely want a no-op can
 *     compare in their own handler.
 *  2. **Availability is PER OPTION.** Preisspiegel greys out Kerzen alone while
 *     more than one channel is visible; a group-level ``disabled`` would strand
 *     the user on whatever presentation they arrived with, unable to switch back.
 *
 * **Caller invariant**: ``value`` should not name a ``disabled`` option. This
 * component is stateless and cannot resolve that itself — it renders exactly what
 * it is given, so a disabled active option shows as an inert grey chip that
 * reports nothing on click. The consumer owns the degrade (Preisspiegel: a
 * ``?pm=kerzen`` arriving while 2 channels are visible resolves to ``linie``
 * WITHOUT clearing the param). The CSS renders that state as an unmistakably
 * inert chip rather than a broken-looking active one, so a consumer that gets it
 * wrong sees it immediately instead of shipping an invisible icon.
 */
import type { ReactNode } from 'react';
import './ChartPresentationToggle.css';

export interface ChartPresentationOption<T extends string> {
  /** The value reported to ``onChange``; also the React key. */
  value: T;
  /** The icon to render. Icon-only by design — pass ``aria-hidden``. */
  icon: ReactNode;
  /** German accessible NAME of this option's button, and its default tooltip. */
  label: string;
  /** True when this presentation is not available for the current data. */
  disabled?: boolean;
  /**
   * German explanation of WHY it is unavailable. AUGMENTS the label in the
   * button's ``title`` rather than replacing it — a disabled icon-only button
   * whose tooltip is only the reason leaves a sighted user with no name for the
   * icon at all (``aria-label`` keeps AT covered either way).
   */
  disabledTitle?: string;
}

export interface ChartPresentationToggleProps<T extends string> {
  /** The presentation currently in effect (already resolved by the caller). */
  value: T;
  options: readonly ChartPresentationOption<T>[];
  onChange: (value: T) => void;
  /** German label for the toolbar group. */
  ariaLabel?: string;
  /**
   * HOF-5 — render each option's ``label`` as VISIBLE text beside its icon,
   * instead of relying on the icon alone.
   *
   * Opt-in, defaulting to the icon-only form, because this component has three
   * consumers and only one of them asked: a pair of unlabelled glyphs is
   * legible enough where the two presentations are a well-known pair, but
   * „line chart vs bar chart" said nothing about what the two actually do to
   * a liquidity curve. The flag keeps that a per-caller decision rather than
   * re-rendering Ernteprognose's and Preisspiegel's toolbars as a side effect.
   *
   * The accessible name is unchanged either way — it has always been the
   * ``label``; this only decides whether a sighted reader gets it too.
   */
  showLabels?: boolean;
}

export default function ChartPresentationToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel = 'Darstellung',
  showLabels = false,
}: ChartPresentationToggleProps<T>) {
  return (
    <div
      className={`chart-presentation-toggle${
        showLabels ? ' chart-presentation-toggle--labelled' : ''
      }`}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="chart-presentation-toggle__btn"
          aria-label={option.label}
          aria-pressed={value === option.value}
          title={
            option.disabled && option.disabledTitle
              ? `${option.label} — ${option.disabledTitle}`
              : option.label
          }
          disabled={option.disabled}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {/* ``aria-hidden``: the button already carries this exact string as its
              ``aria-label``, so exposing the text too would have AT announce the
              name twice. */}
          {showLabels && (
            <span className="chart-presentation-toggle__label" aria-hidden="true">
              {option.label}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
